
import { targetService, Target, PredictionTarget } from "../../services/targetService";
import { Message } from "../types";
import { chatApi } from "../../services/chatApiService";

// Type guard to check if target is a PredictionTarget
function isPredictionTarget(target: Target | PredictionTarget): target is PredictionTarget {
  return 'yes_image_url' in target && 'no_image_url' in target;
}

export const useRevealHandler = () => {
  /**
   * Handles the "reveal target" flow:
   * 1. Fetch target from Supabase and display to user.
   * 2. ChatContext will then automatically trigger feedback/analysis.
   * 3. Set the reveal state to change post-reveal behavior.
   */
  const handleRevealRequest = async (
    currentSessionId: string | null,
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    setTargetIsRevealed: React.Dispatch<React.SetStateAction<boolean>>
  ): Promise<string | null> => {
    if (!currentSessionId) return null;
    try {
      // Get the last target for this session (db = source of truth)
      const target = await targetService.getTargetBySession(currentSessionId);
      if (!target) {
        setMessages(prev => [
          ...prev,
          {
            id: `reveal-notarget-${Date.now()}`,
            content: "No target has been generated yet for this session. Please request a target first.",
            type: "system",
            timestamp: Date.now()
          }
        ]);
        return null;
      }

      // Build reveal content based on target type
      let revealContent = '';
      
      if (isPredictionTarget(target)) {
        // Handle prediction target reveal
        console.log('🔮 REVEAL: Creating prediction target content');
        const event = target.event;
        revealContent = `PREDICTION_TARGET:${JSON.stringify({
          id: target.id,
          target_code: target.target_code,
          yes_image_url: target.yes_image_url,
          no_image_url: target.no_image_url,
          yes_unsplash_data: target.yes_unsplash_data,
          no_unsplash_data: target.no_unsplash_data,
          event: event ? {
            id: event.id,
            title: event.title,
            description: event.description,
            close_time: event.close_time,
            yes_outcome_label: event.yes_outcome_label,
            no_outcome_label: event.no_outcome_label,
            kalshi_event_id: event.kalshi_event_id
          } : null
        })}`;
      } else if (target.target_type === 'location') {
        revealContent = `Here's your location target:\n\nMAP_URL:${target.target_url}`;
      } else if (target.target_type === 'image') {
        console.log('🖼️ REVEAL: Creating image target content with URL:', target.target_url);
        revealContent = `Here's your image target:\n\nIMAGE_URL:${target.target_url}`;
        
        if (target.unsplash_data) {
          revealContent += `\n\nUNSPLASH_DATA:${JSON.stringify(target.unsplash_data)}`;
        }
        
        console.log('🖼️ REVEAL: Image target content created:', revealContent);
      } else {
        revealContent = `Here's your target:`;
      }

      // PHASE 1: Persist reveal messages to database using chatApiService with skipAI flag
      console.log('💾 REVEAL: Persisting user reveal request to database');
      const userRevealContent = `Reveal target: ${target.target_code}`;
      await chatApi.sendMessage(currentSessionId, userRevealContent, undefined, true);

      console.log('💾 REVEAL: Persisting system reveal response to database');
      await chatApi.sendMessage(currentSessionId, revealContent, undefined, true);

      // PHASE 3 FIX: Don't add messages to UI state - let real-time subscription handle it
      // This prevents duplicate messages
      console.log('💾 REVEAL: Messages persisted to database, real-time subscription will update UI');

      // Always mark as revealed in db
      await targetService.markTargetRevealed(target.target_code);
      
      // Set the reveal state to change post-reveal behavior
      setTargetIsRevealed(true);

      return target.target_code;
    } catch (error) {
      setMessages(prev => [
        ...prev,
        {
          id: `reveal-error-${Date.now()}`,
          content: "Sorry, I couldn't retrieve the target. Please try again or contact support.",
          type: "system",
          timestamp: Date.now()
        }
      ]);
      return null;
    }
  };

  return { handleRevealRequest };
};
